import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import {
  Search,
  Plus,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  ShoppingCart,
  IndianRupee,
  Clock,
  CheckCircle2,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  X,
  LayoutDashboard,
  FileUp,
  Calendar
} from 'lucide-react';
import styles from './LOIs.module.css';
import Pagination from '../../components/Common/Pagination/Pagination';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';

import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import Footer from '../../components/Common/Footer/Footer';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import { SlidersHorizontal as Filters } from 'lucide-react';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const LOIs = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const pathPrefix = '/projects/lois';
  const pageTitle = 'LOIs (Letter of Intent)';
  const labelBase = 'LOI';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const mainContentRef = useRef(null);
  const isInitialMount = useRef(true);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    vendor_id: '',
    project_id: '',
    status: ''
  });

  const [lois, setLois] = useState([]);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    current_page: 1,
    per_page: 10,
    last_page: 1
  });
  const [filterData, setFilterData] = useState({
    projects: [],
    vendors: []
  });
  const [summaryData, setSummaryData] = useState({
    total_letter_of_intents: 0,
    total_letter_of_intent_value: 0,
    pending_letter_of_intents: 0,
    completed_letter_of_intents: 0
  });

  const [isTableLoading, setIsTableLoading] = useState(true);

  const fetchFilterData = async () => {
    try {
      const [projectsRes, vendorsRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/vendors-dropdown')
      ]);
      setFilterData({
        projects: projectsRes.data.data || [],
        vendors: vendorsRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchLOIs = async () => {
    setIsTableLoading(true);
    try {
      const response = await api.get('/letter-of-intents', {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          ...filters
        },
        skipLoader: true
      });
      if (response.data.success) {
        setLois(response.data.data.data);
        setPaginationData({
          total: response.data.data.total,
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page
        });
      }
    } catch (error) {
      console.error('Error fetching LOIs:', error);
      if (error.response?.status === 404) {
        setLois([]);
        setPaginationData(prev => ({ ...prev, total: 0, last_page: 1 }));
      } else {
        toast.error(`Failed to load ${pageTitle}`);
      }
    } finally {
      setIsTableLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/letter-of-intents-summary');
      if (response.data.success) {
        setSummaryData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  useEffect(() => {
    fetchLOIs();
    fetchSummary();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const scrollTimeout = setTimeout(() => {
      if (mainContentRef.current) {
        mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
    return () => clearTimeout(scrollTimeout);
  }, [currentPage]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      vendor_id: '',
      project_id: '',
      status: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };

    const handleScroll = () => {
      if (openMenuIndex !== null) setOpenMenuIndex(null);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuIndex]);

  const handleViewDetails = (loi) => {
    navigate(`${pathPrefix}/${loi.id}`);
    setOpenMenuIndex(null);
  };

  const toggleActionMenu = (index, e) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 160
      });
      setOpenMenuIndex(index);
    }
  };

  const handleDeleteClick = (loi) => {
    setOpenMenuIndex(null);
    Swal.fire({
      html: `
        <div class="custom-icon-wrapper-swal">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">Delete ${labelBase}?</h2>
        <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
          Are you sure you want to delete ${labelBase} <strong>${loi.po_number}</strong>? This action cannot be undone.
        </p>
        <p style="margin: 0 0 0 0; color: #94a3b8; font-size: 13px;">
          This action will permanently remove this record from the database.
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: `Yes, Delete ${labelBase}`,
      cancelButtonText: 'No, Keep it',
      customClass: {
        popup: 'custom-swal-popup',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
        actions: 'custom-swal-actions-row'
      },
      buttonsStyling: false
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/letter-of-intents/${loi.id}`);
          if (response.data.success) {
            toast.success(`${labelBase} deleted successfully`);
            fetchLOIs(); // Refresh the list
          }
        } catch (error) {
          console.error('Error deleting LOI:', error);
          toast.error(error.response?.data?.message || `Failed to delete ${labelBase}`);
        }
      }
    });
  };

  const metrics = [
    {
      label: 'Total LOIs',
      value: (summaryData?.total_letter_of_intents || 0).toLocaleString(),
      icon: <ShoppingCart size={20} />,
      theme: 'blue'
    },
    {
      label: 'Total LOI Value',
      value: `₹${parseFloat(summaryData?.total_letter_of_intent_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: <IndianRupee size={20} />,
      theme: 'green',
      highlight: true
    },
    {
      label: 'Pending LOIs',
      value: (summaryData?.pending_letter_of_intents || 0).toLocaleString(),
      icon: <Clock size={20} />,
      theme: 'orange'
    },
    {
      label: 'Completed LOIs',
      value: (summaryData?.completed_letter_of_intents || 0).toLocaleString(),
      icon: <CheckCircle2 size={20} />,
      theme: 'purple'
    }
  ];

  const getStatusClass = (status) => {
    if (!status) return styles['status-pending'];
    const s = status.toLowerCase();
    switch (s) {
      case 'completed': return styles['status-completed'];
      case 'partial': return styles['status-partial'];
      case 'review': return styles['status-review'];
      case 'pending': return styles['status-pending'];
      case 'cancelled':
      case 'rejected': return styles['status-cancelled'];
      default: return styles['status-pending'];
    }
  };



  return (
    <div className={styles['po-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']} ref={mainContentRef}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>{pageTitle}</h1>
              <p>Monitor and manage all LOIs issued to vendors across projects.</p>
            </div>
            <button className={styles['add-btn']} onClick={() => navigate(`${pathPrefix}/create`)}>
              <Plus size={20} />
              <span>Add LOI</span>
            </button>
          </div>

          {/* Metrics — collapsible on mobile */}
          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {metrics[0]?.value} · {metrics[2]?.value} pending
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {metrics.map((m, i) => (
                <div key={i} className={`${styles['metric-card']} ${styles[m.theme]} ${m.highlight ? styles['highlight-card'] : ''}`}>
                  <div className={styles['metric-icon-box']}>
                    {m.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{m.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{m.value}</span>
                      {m.subValue && <span className={styles['metric-sub']}>{m.subValue}</span>}
                    </div>
                  </div>
                  {m.highlight && <div className={styles['glow-effect']}></div>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search LOI # or Vendor Name..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(option.value); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>

              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.project_id || filters.vendor_id || filters.status) && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter LOIs</h3>
                  <p>Filter records by project, vendor, or status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(p => p.id === filters.project_id)?.project_name
                    } : null}
                    onChange={(opt) => handleFilterChange('project_id', opt ? opt.value : '')}
                    options={filterData.projects.map(p => ({ value: p.id, label: p.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Vendor</label>
                  <CustomSelect
                    value={filters.vendor_id ? (() => {
                      const v = filterData.vendors.find(vendor => vendor.id === filters.vendor_id);
                      return v ? {
                        value: v.id,
                        label: v.vendor_name + (v.vendor_code ? ` (${v.vendor_code})` : '')
                      } : null;
                    })() : null}
                    onChange={(opt) => handleFilterChange('vendor_id', opt ? opt.value : '')}
                    options={filterData.vendors.map(v => ({ value: v.id, label: v.vendor_name + (v.vendor_code ? ` (${v.vendor_code})` : '') }))}
                    placeholder="All Vendors"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={filters.status ? {
                      value: filters.status,
                      label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1).replace('_', ' ')
                    } : null}
                    onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'design_review', label: 'Design Review' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'manufacturing', label: 'Manufacturing' },
                      { value: 'payment_done', label: 'Payment Done' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ]}
                    placeholder="All Status"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={[styles['custom-table-container'], 'card'].join(' ')}>
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>LOI NUMBER</th>
                    <th>VENDOR NAME</th>
                    <th>PROJECT NAME</th>
                    <th>LOI DATE</th>
                    <th>EXPECTED DELIVERY</th>
                    <th>TOTAL AMOUNT</th>

                    <th>STATUS</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {isTableLoading ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '100px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                          <Loader2 size={40} className={styles['spinner']} style={{ color: '#f97316' }} />
                          <span style={{ color: '#64748b', fontWeight: 600 }}>Syncing LOIs data...</span>
                        </div>
                      </td>
                    </tr>
                  ) : lois.length > 0 ? (
                    lois.map((loi, index) => (
                      <tr key={loi.id}>
                        <td className={styles['id-cell']}>
                          <div className={styles['receipt-info']}>
                            <span
                              className={styles['receipt-text']}
                              onClick={() => navigate(`${pathPrefix}/${loi.id}`)}
                            >
                              {loi.loi_number}
                            </span>
                          </div>
                        </td>
                        <td className={styles['vendor-name']}>{loi.vendor?.vendor_name}</td>
                        <td className={styles['project-name']}>{loi.project?.project_name}</td>
                        <td className={styles['date-cell']}>{loi.loi_date ? new Date(loi.loi_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                        <td className={styles['date-cell']}>
                          {loi.expected_delivery_date ? new Date(loi.expected_delivery_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td className={styles['amount-cell']}>{parseFloat(loi.total_amount || 0).toLocaleString('en-IN')}</td>
                        <td>
                          <span className={`${styles['status-badge']} ${getStatusClass(loi.status)}`}>
                            {loi.status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td className={styles['actions-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${openMenuIndex === loi.id ? styles['active'] : ''}`}
                              onClick={(e) => toggleActionMenu(loi.id, e)}
                            >
                              <MoreVertical size={18} />
                            </button>
                            {openMenuIndex === loi.id && (
                              <div
                                className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                style={{
                                  position: 'fixed',
                                  top: menuPosition.top,
                                  left: menuPosition.left
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button className={styles['dropdown-item']} onClick={() => handleViewDetails(loi)}>
                                  <Eye size={16} /> View
                                </button>
                                <button className={styles['dropdown-item']} onClick={() => navigate(`${pathPrefix}/edit/${loi.id}`)}>
                                  <Pencil size={16} /> Edit
                                </button>

                                <div className={styles['dropdown-divider']}></div>
                                <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDeleteClick(loi)}>
                                  <Trash2 size={16} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <NoData message={`No ${pageTitle} found.`} colSpan={8} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['po-cards']}>
              {isTableLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 size={32} className={styles['spinner']} style={{ color: '#f97316' }} />
                </div>
              ) : lois.length > 0 ? (
                lois.map((loi, index) => (
                  <div key={loi.id} className={styles['po-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-id']} onClick={() => navigate(`${pathPrefix}/${loi.id}`)}>{loi.loi_number || '#N/A'}</span>
                        <span className={styles['card-amount']}>{parseFloat(loi.total_amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                      <div className={`action-wrapper ${styles['action-wrapper']}`}>
                        <button
                          className={`${styles['action-btn']} ${openMenuIndex === loi.id ? styles['active'] : ''}`}
                          onClick={(e) => toggleActionMenu(loi.id, e)}
                        >
                          <MoreVertical size={18} />
                        </button>
                        {openMenuIndex === loi.id && (
                          <div
                            className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                            style={{
                              position: 'fixed',
                              top: menuPosition.top,
                              left: menuPosition.left
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button className={styles['dropdown-item']} onClick={() => handleViewDetails(loi)}>
                              <Eye size={16} /> View
                            </button>
                            <button className={styles['dropdown-item']} onClick={() => navigate(`${pathPrefix}/edit/${loi.id}`)}>
                              <Pencil size={16} /> Edit
                            </button>

                            <div className={styles['dropdown-divider']}></div>
                            <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDeleteClick(loi)}>
                              <Trash2 size={16} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>Vendor</span>
                        <span className={styles['detail-value']}>{loi.vendor?.vendor_name}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Expected</span>
                        <span className={styles['detail-value']}>{loi.expected_delivery_date ? new Date(loi.expected_delivery_date).toLocaleDateString('en-IN') : '—'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Status</span>
                        <span className={styles['detail-value']}>{loi.status?.toUpperCase()}</span>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <div className={styles['footer-left']}>
                        <Calendar size={14} className={styles['text-muted']} />
                        <span>{loi.loi_date ? new Date(loi.loi_date).toLocaleDateString('en-IN') : '—'}</span>
                      </div>
                      <div className={styles['footer-statuses']}>
                        <span className={`${styles['status-pill']} ${styles['pill-']} ${styles[loi.status]}`}>
                          {loi.status?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <NoData message={`No ${pageTitle} found.`} isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={paginationData.current_page}
                totalItems={paginationData.total}
                itemsPerPage={paginationData.per_page}
                onPageChange={(page) => { setCurrentPage(page); }}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={paginationData.current_page}
              totalItems={paginationData.total}
              itemsPerPage={paginationData.per_page}
              onPageChange={(page) => { setCurrentPage(page); }}
            />
          </div>

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default LOIs;
